import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, retry } from 'rxjs';

import { LoginService } from '../pages/login/services/login.service';
import { GetTokenZegoResponse } from '../pages/login/types/login';
import { addWebSuffix } from '../utils/string.util';
import { getUserInfo } from '../utils/utils';
import { AuthSessionService } from './auth-session.service';
import { BroadcastService } from './broadcast.service';
import { LogoutService } from './logout.service';
import { ZegoEngineService } from './zego-engine.service';
import { ZimService } from './zim.service';

@Injectable({ providedIn: 'root' })
export class TokenRenewService implements OnDestroy {
  private refreshingToken = false;
  private epochInMilliseconds = Date.now();
  private kickoutTime = 0;
  private isForceLoggingOut = false;
  private apiCallTimeout?: ReturnType<typeof setTimeout>;

  private readonly zimService = inject(ZimService);
  private readonly loginService = inject(LoginService);
  private readonly toastService = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly broadcastService = inject(BroadcastService);
  private readonly zegoEngineService = inject(ZegoEngineService);
  private readonly logoutService = inject(LogoutService);
  private readonly authSession = inject(AuthSessionService);

  /** Promise của request /meeting/zego/token đang chạy (nếu có), null khi không có. */
  private inFlightRefresh: Promise<void> | null = null;

  /** Thời điểm (ms) lần lấy token gần nhất hoàn tất. Dùng để chặn fetch lặp ngay sau đó. */
  private lastRefreshAt = 0;

  /**
   * Trong khoảng này sau một lần lấy token, mọi refreshZegoToken khác sẽ bị bỏ qua.
   * Mục đích: khi mới vào/refresh trang, đường "chủ động" (app.component) và đường
   * "reactive" (event tokenExpired/tokenWillExpire từ ZIM) không cùng gọi API 2 lần.
   */
  private static readonly REFRESH_COOLDOWN_MS = 3000;

  /** Chu kỳ tự động lấy token mới: 1 giờ kể từ lần lấy token gần nhất. */
  private static readonly RENEW_INTERVAL_MS = 60 * 60 * 1000;

  private renewTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Mốc (ms) mà lần renew định kỳ kế tiếp phải chạy.
   * Giữ lại kể cả khi tab bị ẩn (timeout đã bị xoá) để lúc quay lại biết còn bao lâu.
   * 0 = chưa từng lấy token nên chưa bật chu kỳ.
   */
  private nextRenewAt = 0;

  constructor() {
    this.registerHandlers();
  }

  registerHandlers() {
    this.zimService.tokenWillExpire$.subscribe(() => {
      this.refreshZegoToken(true).then();
    });

    this.zimService.apiCallError$.subscribe((res) => {
      // 6000121: User is not logged in (connections probably happened)
      if (res.code === 6000121) {
        this.apiCallTimeout ??= setTimeout(() => {
          if (Date.now() - this.kickoutTime >= 5000) {
            this.refreshZegoToken(false, false).then();
          }
          this.apiCallTimeout = undefined;
        }, 2000);
      }
    });

    this.zimService.apiCallError$.subscribe((res) => {
      // 6000103: Token is invalid.
      // 6000106: Token expired.
      if (res.code === 6000103 || res.code === 6000106) {
        // token is invalid: an empty token was passed
        this.handleInvalidOrExpiredToken();
      }
    });

    this.zimService.kickedOut$.subscribe(() => {
      this.kickoutTime = Date.now();
      this.handleLoggedOut();
    });

    this.zimService.forceLogout$.subscribe(() => this.handleForceLogout());

    this.zimService.tokenExpired$.subscribe(() => {
      // incase disconnected need login again
      this.refreshingToken = false;
      this.refreshZegoToken(false, false, true).then();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Tab bị ẩn -> xoá timeout (setTimeout bị browser throttle khi tab background).
   * Tab hiện lại -> nếu đã quá hạn thì lấy token mới ngay, chưa thì hẹn lại phần còn lại.
   */
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.clearRenewTimeout();
      return;
    }

    // Chưa từng lấy token -> chưa bật chu kỳ.
    if (!this.nextRenewAt) {
      return;
    }

    const remaining = this.nextRenewAt - Date.now();
    if (remaining <= 0) {
      this.runPeriodicRenew().then();
    } else {
      this.scheduleTokenRenew(remaining);
    }
  };

  private isInMeeting() {
    return !!sessionStorage.getItem('token_zego_w');
  }

  private clearRenewTimeout() {
    if (this.renewTimeout) {
      clearTimeout(this.renewTimeout);
      this.renewTimeout = undefined;
    }
  }

  /** Dừng hẳn chu kỳ renew định kỳ (không còn mốc để visibilitychange bám vào). */
  private stopTokenRenew() {
    this.clearRenewTimeout();
    this.nextRenewAt = 0;
  }

  /** Hẹn lần renew định kỳ kế tiếp (mặc định 1h kể từ bây giờ). */
  private scheduleTokenRenew(delayMs = TokenRenewService.RENEW_INTERVAL_MS) {
    this.clearRenewTimeout();
    this.nextRenewAt = Date.now() + delayMs;

    // Tab đang ẩn: chỉ ghi nhận mốc, không tạo timeout (sẽ hẹn lại khi visible).
    if (document.visibilityState === 'hidden') {
      return;
    }

    this.renewTimeout = setTimeout(() => {
      this.renewTimeout = undefined;
      this.runPeriodicRenew().then();
    }, delayMs);
  }

  private async runPeriodicRenew() {
    this.clearRenewTimeout();

    // Đang trong meeting -> dừng chu kỳ, không renew token chính.
    if (this.isInMeeting()) {
      console.log('[Token-Renew] stop periodic renew (in meeting)');
      this.stopTokenRenew();
      return;
    }

    await this.refreshZegoToken(true);

    // refreshZegoToken đã hẹn lại timer trong finally; nếu nó return sớm
    // (đang có request chạy / trong cooldown) thì tự hẹn lại ở đây.
    if (!this.renewTimeout) {
      this.scheduleTokenRenew();
    }
  }

  /**
   * Chờ cho tới khi request /meeting/zego/token đang chạy (nếu có) hoàn tất.
   * Resolve ngay nếu hiện tại không có request nào đang chạy.
   */
  async whenTokenReady(): Promise<void> {
    while (this.inFlightRefresh) {
      await this.inFlightRefresh;
    }
  }

  /**
   * Lấy token cho luồng join meeting (identity web-suffix) DƯỚI CÙNG khoá
   * refreshingToken/inFlightRefresh với refreshZegoToken.
   *
   * Nhờ vậy trong lúc request token này đang chạy, mọi refreshZegoToken khác sẽ
   * dùng lại promise đang chạy (guard `if (this.refreshingToken())`) thay vì gọi
   * API token lần nữa -> token vừa lấy không bị vô hiệu (expired).
   */
  async getMeetingToken(webUserId: string, fromMeeting?: boolean): Promise<string | null> {
    // Chờ request token đang chạy (nếu có) để không chồng lệnh.
    await this.whenTokenReady();

    let token: string | null = null;
    this.refreshingToken = true;
    this.inFlightRefresh = (async () => {
      const res = await firstValueFrom(this.loginService.getTokenZego(webUserId));
      if (!res || !res.success) {
        return;
      }
      token = res.data.data;
      sessionStorage.setItem(fromMeeting ? 'token_zego_w' : 'token_zego', token);
    })().finally(() => {
      this.refreshingToken = false;
      this.inFlightRefresh = null;
      this.lastRefreshAt = Date.now();
    });

    await this.inFlightRefresh;
    return token;
  }

  private refreshZegoToken(
    isRenew: boolean,
    showToast = false,
    shouldRedirectLogin = false,
  ): Promise<void> {
    if (this.refreshingToken) {
      // Đang có request chạy: trả về chính promise đó để caller có thể chờ.
      return this.inFlightRefresh ?? Promise.resolve();
    }

    // Vừa lấy token xong (ví dụ: initial login ở app.component) -> bỏ qua để không gọi API lần 2.
    if (Date.now() - this.lastRefreshAt < TokenRenewService.REFRESH_COOLDOWN_MS) {
      return Promise.resolve();
    }

    this.refreshingToken = true;
    this.inFlightRefresh = this.doRefreshZegoToken(isRenew, showToast, shouldRedirectLogin).finally(
      () => {
        this.refreshingToken = false;
        this.inFlightRefresh = null;
        this.lastRefreshAt = Date.now();
        // Đang trong meeting: token lấy được là token_zego_w, không thuộc chu kỳ
        // renew của token chính -> dừng hẳn chu kỳ.
        // Ngoài meeting: đếm lại 1h cho lần renew kế tiếp.
        if (this.isInMeeting()) {
          this.stopTokenRenew();
        } else {
          this.scheduleTokenRenew();
        }
      },
    );
    return this.inFlightRefresh;
  }

  private async doRefreshZegoToken(
    isRenew: boolean,
    showToast = false,
    shouldRedirectLogin = false,
  ) {
    const userInfo = getUserInfo();

    const { zegoId } = userInfo;
    console.log('[Token-Renew] isRenew', isRenew);

    let res: GetTokenZegoResponse;
    const isInMeeting = !!sessionStorage.getItem('token_zego_w');
    try {
      res = await firstValueFrom(
        this.loginService
          .getTokenZego(isInMeeting ? addWebSuffix(zegoId) : zegoId)
          .pipe(retry({ delay: 10000, count: 3 })),
      );
    } catch (error) {
      this.refreshingToken = false;
      if (shouldRedirectLogin) {
        this.redirectLogin();
      }
      return;
    }

    try {
      sessionStorage.setItem(isInMeeting ? 'token_zego_w' : 'token_zego', res.data.data);
      if (isRenew) {
        await this.zimService.getZIM()?.renewToken(res.data.data);
        await this.zegoEngineService.engine?.renewToken(res.data.data);
      } else {
        const loginId = isInMeeting ? addWebSuffix(zegoId) : zegoId;
        await this.zimService.login(loginId, res.data.data);
        if (showToast) {
          this.toastService.info('Đã kết nối lại', undefined, { timeOut: 5000 });
        }
      }
    } catch (err: any) {
      // TODO(ducvm18): Handle 6000105 "Request timeout"
      console.error('Failed to get zego token', err);
    } finally {
      this.refreshingToken = false;
    }
  }

  private redirectLogin() {
    this.zimService.logout();
    this.loginService.clearStorage();
    this.broadcastService.logout();

    this.router
      .navigateByUrl('/login', {
        replaceUrl: true,
        skipLocationChange: false,
      })
      .then(() => {
        // * hientt: reset state in chat.store.ts
        window.location.reload();
      });
  }

  handleLoggedOut() {
    /**
     * We may be kicked early into the app (no token yet?), this is normal.
     * If we're in that phase (5s after this service's init), we can just do a reconnection flow.
     */

    const now = Date.now();
    if (now - this.epochInMilliseconds > 5000) {
      alert('Bạn đang đăng nhập trên quá nhiều máy! Bấm OK hoặc Close để sử dụng trên tab này.');
    }
    this.epochInMilliseconds = now;

    this.refreshZegoToken(false, true).then();
  }

  /** Phiên web này bị đăng xuất từ thiết bị khác (lệnh FORCE_LOGOUT qua ZIM). */
  handleForceLogout() {
    if (this.isForceLoggingOut || this.logoutService.isSelfLoggingOut) return;
    this.isForceLoggingOut = true;

    alert('Phiên đăng nhập đã bị đăng xuất từ thiết bị khác. Vui lòng đăng nhập lại.');
    // Giống app Android khi bị đá thiết bị: vẫn gọi API logout rồi xoá phiên.
    this.authSession.forceLogout();
  }

  handleInvalidOrExpiredToken() {
    this.refreshZegoToken(false).then();
  }

  login() {
    return this.refreshZegoToken(false);
  }

  ngOnDestroy() {
    this.clearRenewTimeout();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
